import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  User,
  Shield,
  Mail,
  Phone,
  Building,
  Key,
  AlertTriangle,
  CheckCircle,
  UserCircle,
  Briefcase,
  Calendar,
  Globe,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { authAPI } from "@/services/api";
import { InventoryAlertSettingsCard } from "@/components/inventory/InventoryAlertSettingsCard";

export default function Settings() {
  const { user, company } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>(
    {}
  );
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const handleTwoFactorToggle = () => {
    const newValue = !twoFactor;
    setTwoFactor(newValue);

    toast({
      title: newValue
        ? "Autenticación 2FA activada"
        : "Autenticación 2FA desactivada",
      description: newValue
        ? "Se ha habilitado la autenticación de dos factores"
        : "Se ha deshabilitado la autenticación de dos factores",
      variant: "default",
    });
  };

  const validatePassword = () => {
    const errors: Record<string, string> = {};

    if (!passwordForm.currentPassword) {
      errors.currentPassword = "La contraseña actual es requerida";
    }

    if (!passwordForm.newPassword) {
      errors.newPassword = "La nueva contraseña es requerida";
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = "La contraseña debe tener al menos 8 caracteres";
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = "Las contraseñas no coinciden";
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordChange = async () => {
    if (!validatePassword()) {
      toast({
        title: "Error de validación",
        description: "Por favor, corrige los errores en el formulario",
        variant: "destructive",
      });
      return;
    }

    if (!user?.id) return;

    setLoading(true);

    try {
      await authAPI.changePassword({
        user_id: user.id,
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });

      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setShowPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      toast({
        title: "Contraseña actualizada",
        description: "Tu contraseña ha sido cambiada exitosamente",
        variant: "default",
      });
    } catch (error: unknown) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Error al cambiar la contraseña",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    toast({
      title: "Solicitud recibida",
      description: "Tu cuenta será eliminada en 30 días",
      variant: "destructive",
    });
    setIsDeleteOpen(false);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <User className="h-12 w-12 text-gray-300 mx-auto" />
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay usuario
            </h3>
            <p className="text-gray-600">
              Inicia sesión para ver la configuración
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuración</h1>
          <p className="text-slate-500 mt-2">
            Gestiona tu perfil y preferencias de cuenta
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-slate-200 shadow-lg">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="flex items-center text-xl text-slate-900">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg mr-3">
                  <UserCircle className="h-6 w-6" />
                </div>
                Información del Perfil
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Nombre
                  </Label>
                  <div className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <User className="h-5 w-5 text-slate-400 mr-3" />
                    <span className="font-medium text-slate-900">
                      {user.first_name} {user.last_name}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center">
                    Correo Electrónico
                  </Label>
                  <div className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <Mail className="h-5 w-5 text-slate-400 mr-3" />
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-900">
                        {user.email}
                      </span>
                      <span className="text-xs text-slate-500">Principal</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Rol
                  </Label>
                  <div className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <Briefcase className="h-5 w-5 text-slate-400 mr-3" />
                    <div className="flex flex-col">
                      <Badge
                        variant="secondary"
                        className="w-fit bg-slate-200 text-slate-700 hover:bg-slate-300"
                      >
                        {user.role?.name || "Usuario"}
                      </Badge>
                      {user.is_owner && (
                        <span className="text-xs text-slate-500 mt-1">
                          Propietario de la cuenta
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700 flex items-center">
                    Teléfono
                  </Label>
                  <div className="flex items-center p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <Phone className="h-5 w-5 text-slate-400 mr-3" />
                    <span className="font-medium text-slate-900">
                      {user.phone || "No especificado"}
                    </span>
                  </div>
                </div>
              </div>

              {company && (
                <>
                  <Separator className="my-8 bg-slate-100" />
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center">
                      <div className="p-2 bg-purple-100 text-purple-600 rounded-lg mr-3">
                        <Building className="h-5 w-5" />
                      </div>
                      Información de la Empresa
                    </h3>
                    <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-slate-500 mb-1">Nombre</p>
                          <p className="font-semibold text-slate-900 text-lg">
                            {company.name}
                          </p>
                          <p className="text-sm text-slate-500 mt-1">
                            {company.email}
                          </p>
                        </div>
                        <div className="md:text-right">
                          <p className="text-sm text-slate-500 mb-1">
                            Identificador
                          </p>
                          <code className="text-sm bg-white px-3 py-1 rounded-lg border border-slate-200 text-slate-700 font-mono">
                            {company.slug}
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8">
                    <InventoryAlertSettingsCard />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="border-slate-200 shadow-lg">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="flex items-center text-lg text-slate-900">
                  <div className="p-2 bg-orange-100 text-orange-600 rounded-lg mr-3">
                    <Key className="h-5 w-5" />
                  </div>
                  Seguridad
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <input type="text" className="hidden" autoComplete="username" />
                <input
                  type="password"
                  className="hidden"
                  autoComplete="new-password"
                />

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Contraseña Actual
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={passwordForm.currentPassword}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          currentPassword: e.target.value,
                        })
                      }
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && (
                    <p className="text-xs text-red-500">
                      {passwordErrors.currentPassword}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Nueva Contraseña
                  </Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordForm.newPassword}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          newPassword: e.target.value,
                        })
                      }
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.newPassword && (
                    <p className="text-xs text-red-500">
                      {passwordErrors.newPassword}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    Confirmar Contraseña
                  </Label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwordForm.confirmPassword}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          confirmPassword: e.target.value,
                        })
                      }
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && (
                    <p className="text-xs text-red-500">
                      {passwordErrors.confirmPassword}
                    </p>
                  )}
                </div>

                <Button
                  className="w-full mt-2"
                  onClick={handlePasswordChange}
                  disabled={loading}
                >
                  {loading ? "Actualizando..." : "Cambiar Contraseña"}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-red-100 shadow-lg overflow-hidden">
              <CardHeader className="bg-red-50/50 border-b border-red-100 pb-4">
                <CardTitle className="flex items-center text-lg text-red-700">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Zona de Peligro
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                      Eliminar tu cuenta es una acción permanente. Todos tus
                      datos serán eliminados y no podrán recuperarse.
                    </p>
                    <Button
                      variant="destructive"
                      className="w-full bg-red-600 hover:bg-red-700 shadow-lg shadow-red-900/20"
                      onClick={() => setIsDeleteOpen(true)}
                    >
                      Eliminar Cuenta Permanentemente
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás absolutamente seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              tu cuenta y eliminará tus datos de nuestros servidores.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar Cuenta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
